import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { logout as authLogout } from '../services/authService';
import { saveFcmToken } from '../services/fcmTokenService';
import { bootChannelTalk, shutdownChannelTalk } from '../services/channelTalkService';
import { getConsentGateState, recordRequiredConsents } from '../services/consentService';
import { auditConsentRecorded, auditLogout, auditSessionStarted } from '../services/auditLogService';

// Firebase Auth UID로 users 문서 찾기
// 1) users/{authUid} 직접 → 2) linkedSocialUid 역탐색 → 3) linkedEmailUid 역탐색
const resolveUserDoc = async (authUid) => {
  const direct = await getDoc(doc(db, 'users', authUid));
  if (direct.exists()) {
    const data = direct.data();
    if (data.role) return { uid: direct.id, ...data };
  }

  const socialQ = query(collection(db, 'users'), where('linkedSocialUid', '==', authUid));
  const socialSnap = await getDocs(socialQ);
  if (!socialSnap.empty) {
    const d = socialSnap.docs[0];
    return { uid: d.id, ...d.data() };
  }

  const emailQ = query(collection(db, 'users'), where('linkedEmailUid', '==', authUid));
  const emailSnap = await getDocs(emailQ);
  if (!emailSnap.empty) {
    const d = emailSnap.docs[0];
    return { uid: d.id, ...d.data() };
  }

  return null;
};

// 역할별 상세 문서 병합
const mergeRoleDoc = async (ud) => {
  try {
    if (ud.role === 'partner') {
      const pSnap = await getDoc(doc(db, 'partners', ud.uid));
      if (pSnap.exists()) Object.assign(ud, pSnap.data());
    } else if (ud.role === 'realtor') {
      const rSnap = await getDoc(doc(db, 'realtors', ud.uid));
      if (rSnap.exists()) Object.assign(ud, rSnap.data());
    }
  } catch { /* skip */ }
  return ud;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const EMPTY_CONSENT = {
  requiredTerms: [],
  missingRequiredTerms: [],
  requiredConsentKeys: [],
  requiresConsent: false,
  consentCompletedAt: '',
};

export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consentState, setConsentState] = useState(EMPTY_CONSENT);
  const [consentLoading, setConsentLoading] = useState(true);

  const hydrateConsent = useCallback(async (ud) => {
    if (!ud?.role) {
      setConsentState(EMPTY_CONSENT);
      return EMPTY_CONSENT;
    }
    try {
      const next = await getConsentGateState({ profile: ud, role: ud.role });
      setConsentState(next);
      return next;
    } catch (err) {
      console.error('약관 동의 상태 조회 실패:', err);
      setConsentState(EMPTY_CONSENT);
      return EMPTY_CONSENT;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const base = await resolveUserDoc(firebaseUser.uid);
          if (base) {
            const ud = await mergeRoleDoc(base);
            setUserData(ud);
            void auditSessionStarted(ud, { authUid: firebaseUser.uid });

            setConsentLoading(true);
            await hydrateConsent(ud);
            setConsentLoading(false);

            // RN에서 주입한 FCM 토큰이 localStorage에 있으면 저장
            try {
              const fcmToken = localStorage.getItem('ieum.fcm.token');
              const fcmPlatform = localStorage.getItem('ieum.fcm.platform') || 'android';
              if (fcmToken && ud.uid) {
                saveFcmToken(ud.uid, fcmToken, fcmPlatform);
              }
            } catch {}

            // 채널톡 SDK boot — 부동산/제휴업체만
            bootChannelTalk(ud.role, {
              uid: ud.uid,
              name: ud.name,
              phone: ud.phone,
              email: ud.email,
            });
          } else {
            setUserData(null);
            setConsentState(EMPTY_CONSENT);
            setConsentLoading(false);
            shutdownChannelTalk();
          }
        } catch (err) {
          console.error('유저 정보 조회 실패:', err);
          setUserData(null);
          setConsentState(EMPTY_CONSENT);
          setConsentLoading(false);
        }
      } else {
        setUserData(null);
        setConsentState(EMPTY_CONSENT);
        setConsentLoading(false);
        shutdownChannelTalk();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [hydrateConsent]);

  const refreshSession = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    const base = await resolveUserDoc(user.uid);
    if (base) {
      const ud = await mergeRoleDoc(base);
      setUserData(ud);
      await hydrateConsent(ud);
    }
  }, [hydrateConsent]);

  const recordConsents = useCallback(async (terms, method = 'consent_gate') => {
    const user = auth.currentUser;
    if (!user || !userData) throw new Error('로그인된 사용자가 필요합니다.');
    await recordRequiredConsents({
      user, profile: userData, role: userData.role, terms, method,
    });
    void auditConsentRecorded(userData, {
      method,
      termKeys: Array.isArray(terms) ? terms.map((term) => term?.key || term?.id || '').filter(Boolean) : [],
    });
    // 저장 후 프로필 새로고침 + 동의 상태 재계산
    await refreshSession();
  }, [userData, refreshSession]);

  const signOut = useCallback(async () => {
    void auditLogout(userData);
    await authLogout();
    setUserData(null);
    setConsentState(EMPTY_CONSENT);
    shutdownChannelTalk();
  }, [userData]);

  const value = {
    user: userData,
    userData,
    loading,
    role: userData?.role || null,
    isAdmin: userData?.role === 'admin',
    isStaff: userData?.role === 'staff' || userData?.role === 'admin',
    isPartner: userData?.role === 'partner',
    isRealtor: userData?.role === 'realtor',
    // 약관 동의 게이트
    consentLoading,
    requiredTerms: consentState.requiredTerms,
    missingRequiredTerms: consentState.missingRequiredTerms,
    requiredConsentKeys: consentState.requiredConsentKeys,
    requiresConsent: consentState.requiresConsent,
    consentCompletedAt: consentState.consentCompletedAt,
    recordConsents,
    refreshSession,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
